import { Contract } from '@ethersproject/contracts'
import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Badge,
  CardDescription,
  CardHeader,
  CardTitle,
  ExecuteButton,
  FunctionCardContainer,
  InputGroup,
  InputLabel,
  LoadingSpinner,
  ResultDisplay,
  StyledInput,
} from '../styleds'

interface AbiInput {
  name: string
  type: string
  indexed?: boolean
  components?: AbiInput[]
}

interface AbiItem {
  type: string
  name?: string
  inputs?: AbiInput[]
  outputs?: AbiInput[]
  stateMutability?: string
}

interface ContractWidgetProps {
  title: string
  description: string
  contract: Contract | null
  abi: AbiItem[]
  defaultFunction?: string
}

// Skip internal/system functions
const SKIP_FUNCTIONS = new Set([
  'initialize',
  'grantRole',
  'revokeRole',
  'renounceRole',
  'supportsInterface',
  'getRoleAdmin',
  'hasRole',
  'DEFAULT_ADMIN_ROLE',
  'SYSTEM',
  'NATIVE_TOKEN_CONTRACT',
  'NATIVE_TRANSFER_PRECOMPILE',
  'NATIVE_TRANSFER_PRECOMPILE_GAS',
  'VALIDATOR_PKCHECK_PRECOMPILE',
  'VALIDATOR_PKCHECK_PRECOMPILE_GAS',
])

export default function ContractWidget({ title, description, contract, abi, defaultFunction }: ContractWidgetProps) {
  const { account } = useWeb3React()

  // Extract functions from ABI
  const { readFunctions, writeFunctions } = useMemo(() => {
    const read: AbiItem[] = []
    const write: AbiItem[] = []

    for (const item of abi) {
      if (item.type !== 'function' || !item.name) continue
      if (SKIP_FUNCTIONS.has(item.name)) continue

      const mutability = item.stateMutability || ''
      if (mutability === 'view' || mutability === 'pure') {
        read.push(item)
      } else {
        write.push(item)
      }
    }

    // Sort alphabetically
    read.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    write.sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    return { readFunctions: read, writeFunctions: write }
  }, [abi])

  const [selectedFunction, setSelectedFunction] = useState<string>(defaultFunction || '')
  const [isReadMode, setIsReadMode] = useState(true)
  const [params, setParams] = useState<Record<string, string>>({})
  const [isExecuting, setIsExecuting] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Get current function info
  const currentFunction = useMemo(() => {
    const functions = isReadMode ? readFunctions : writeFunctions
    return functions.find((f) => f.name === selectedFunction)
  }, [selectedFunction, isReadMode, readFunctions, writeFunctions])

  // Reset params when function changes
  useEffect(() => {
    setParams({})
    setResult(null)
    setError(null)
  }, [selectedFunction])

  // Set default function when mode changes
  useEffect(() => {
    const functions = isReadMode ? readFunctions : writeFunctions
    if (functions.length > 0 && !functions.find((f) => f.name === selectedFunction)) {
      setSelectedFunction(functions[0].name || '')
    }
  }, [isReadMode, readFunctions, writeFunctions, selectedFunction])

  const handleInputChange = useCallback((name: string, value: string) => {
    setParams((prev) => ({ ...prev, [name]: value }))
    setError(null)
  }, [])

  const parseInputValue = useCallback((type: string, value: string): any => {
    if (!value && value !== '0') return undefined

    // Handle arrays
    if (type.endsWith('[]')) {
      return value.split(',').map((v) => v.trim())
    }

    // Handle booleans
    if (type === 'bool') {
      return value.toLowerCase() === 'true' || value === '1'
    }

    // Handle tuples (pass as-is, user provides JSON)
    if (type.startsWith('tuple')) {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }

    // Everything else (uint256, address, bytes, string)
    return value
  }, [])

  const formatResult = useCallback((response: any): string => {
    if (response === null || response === undefined) {
      return 'null'
    }

    // BigNumber
    if (typeof response === 'object' && response._isBigNumber) {
      return response.toString()
    }

    // Array
    if (Array.isArray(response)) {
      const formatted = response.map((item) => {
        if (typeof item === 'object' && item._isBigNumber) {
          return item.toString()
        }
        return item
      })
      return JSON.stringify(formatted, null, 2)
    }

    // Object (struct)
    if (typeof response === 'object') {
      const formatted: Record<string, any> = {}
      for (const key of Object.keys(response)) {
        // Skip numeric keys (array indices)
        if (!isNaN(Number(key))) continue
        const val = response[key]
        if (typeof val === 'object' && val._isBigNumber) {
          formatted[key] = val.toString()
        } else {
          formatted[key] = val
        }
      }
      return JSON.stringify(formatted, null, 2)
    }

    return String(response)
  }, [])

  const handleExecute = useCallback(async () => {
    if (!contract || !currentFunction?.name) {
      setError('Contract or function not available')
      return
    }

    setIsExecuting(true)
    setError(null)
    setResult(null)

    try {
      const inputs = currentFunction.inputs || []
      const args = inputs.map((input) => parseInputValue(input.type, params[input.name] || ''))
      const filteredArgs = args.filter((arg) => arg !== undefined)

      if (isReadMode) {
        const response = await contract[currentFunction.name](...filteredArgs)
        setResult(formatResult(response))
      } else {
        // Check if function is payable and add value if needed
        let options = {}
        if (currentFunction.stateMutability === 'payable' && params['_value']) {
          options = { value: params['_value'] }
        }

        const tx = await contract[currentFunction.name](...filteredArgs, options)
        setResult(`Transaction submitted: ${tx.hash}`)
        await tx.wait()
        setResult(`Transaction confirmed: ${tx.hash}`)
      }
    } catch (err: any) {
      console.error('Function execution error:', err)
      setError(err.reason || err.message || 'Execution failed')
    } finally {
      setIsExecuting(false)
    }
  }, [contract, currentFunction, params, parseInputValue, formatResult, isReadMode])

  const isDisabled = !contract || (!isReadMode && !account) || isExecuting || !selectedFunction

  const activeFunctions = isReadMode ? readFunctions : writeFunctions

  return (
    <FunctionCardContainer style={{ gridColumn: 'span 2' }}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Badge
            variant="read"
            onClick={() => setIsReadMode(true)}
            style={{ cursor: 'pointer', opacity: isReadMode ? 1 : 0.5 }}
          >
            Read ({readFunctions.length})
          </Badge>
          <Badge
            variant="write"
            onClick={() => setIsReadMode(false)}
            style={{ cursor: 'pointer', opacity: !isReadMode ? 1 : 0.5 }}
          >
            Write ({writeFunctions.length})
          </Badge>
        </div>
      </CardHeader>

      <CardDescription>{description}</CardDescription>

      {/* Function Selector */}
      <InputGroup>
        <InputLabel>Function</InputLabel>
        <select
          value={selectedFunction}
          onChange={(e) => setSelectedFunction(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.05)',
            color: 'inherit',
            fontSize: '14px',
          }}
        >
          {activeFunctions.map((fn) => (
            <option key={fn.name} value={fn.name}>
              {fn.name}({(fn.inputs || []).map((i) => `${i.name}: ${i.type}`).join(', ')})
            </option>
          ))}
        </select>
      </InputGroup>

      {/* Dynamic Inputs */}
      {currentFunction?.inputs?.map((input) => (
        <InputGroup key={input.name}>
          <InputLabel>
            {input.name} <span style={{ opacity: 0.6, fontSize: '12px' }}>({input.type})</span>
          </InputLabel>
          <StyledInput
            type="text"
            placeholder={getPlaceholder(input.type)}
            value={params[input.name] || ''}
            onChange={(e) => handleInputChange(input.name, e.target.value)}
          />
        </InputGroup>
      ))}

      {/* Value input for payable functions */}
      {!isReadMode && currentFunction?.stateMutability === 'payable' && (
        <InputGroup>
          <InputLabel>
            Value (wei) <span style={{ opacity: 0.6, fontSize: '12px' }}>(payable)</span>
          </InputLabel>
          <StyledInput
            type="text"
            placeholder="Amount in wei to send"
            value={params['_value'] || ''}
            onChange={(e) => handleInputChange('_value', e.target.value)}
          />
        </InputGroup>
      )}

      <ExecuteButton onClick={handleExecute} disabled={isDisabled}>
        {isExecuting && <LoadingSpinner />}
        {isExecuting ? <Trans>Executing...</Trans> : isReadMode ? <Trans>Query</Trans> : <Trans>Execute</Trans>}
      </ExecuteButton>

      {result && (
        <ResultDisplay
          style={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'monospace',
            fontSize: '12px',
          }}
        >
          {result}
        </ResultDisplay>
      )}
      {error && <ResultDisplay error>{error}</ResultDisplay>}
    </FunctionCardContainer>
  )
}

function getPlaceholder(type: string): string {
  if (type === 'address') return '0x...'
  if (type === 'uint256' || type === 'uint128' || type === 'uint64') return 'e.g., 1000000000000000000'
  if (type === 'bool') return 'true or false'
  if (type === 'bytes' || type === 'bytes32') return '0x...'
  if (type.endsWith('[]')) return 'comma-separated values'
  if (type.startsWith('tuple')) return 'JSON object/array'
  return `Enter ${type}`
}
